import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { QuestionDetailContent } from "@/components/questions/question-detail-content";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { type QuestionPriority, type QuestionRow, useQuestions } from "@/hooks/use-questions";
import { cn } from "@/lib/utils";

const PRIORITY_TINTS: Record<QuestionPriority, string> = {
  high: "bg-warning-bg text-warning",
  med: "bg-info-bg text-info",
  low: "bg-muted text-foreground/60",
};

interface LinkedQuestionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  solutionId: string;
  questionIds: string[];
}

export function LinkedQuestionsSheet({
  open,
  onOpenChange,
  solutionId,
  questionIds,
}: LinkedQuestionsSheetProps) {
  const { data: allQuestions, isLoading } = useQuestions(solutionId);

  const linked: QuestionRow[] = useMemo(() => {
    if (!allQuestions) return [];
    const set = new Set(questionIds);
    return allQuestions.filter((q) => set.has(q.id));
  }, [allQuestions, questionIds]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Auto-select first whenever linked set changes (or on first open)
  useEffect(() => {
    if (linked.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !linked.some((q) => q.id === selectedId)) {
      setSelectedId(linked[0]?.id ?? null);
    }
  }, [linked, selectedId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[680px] overflow-hidden p-0 flex flex-col"
      >
        <SheetHeader className="border-b border-[var(--color-border)] px-6 py-4 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle className="font-display text-base">
              Linked questions
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                ({linked.length})
              </span>
            </SheetTitle>
            {selectedId && (
              <Link
                to="/s/$solutionId/questions"
                params={{ solutionId }}
                search={{ q: selectedId }}
                className="inline-flex items-center gap-1 text-xs text-info hover:underline"
                title="Open this question on the Questions page"
              >
                Open full view
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
          <SheetDescription className="text-xs">
            Edit priority, status, and the answer here, or open the full view for the dedicated
            page.
          </SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="p-6 space-y-2">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {!isLoading && linked.length === 0 && (
          <div className="p-8 text-sm text-muted-foreground text-center">No linked questions.</div>
        )}

        {!isLoading && linked.length > 0 && (
          <>
            {/* Picker — horizontal scrolling list of questions when there's more than one */}
            {linked.length > 1 && (
              <nav className="border-b border-[var(--color-border)] bg-muted/30 px-3 py-2 overflow-x-auto shrink-0">
                <ul className="flex gap-1.5">
                  {linked.map((q) => (
                    <li key={q.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(q.id)}
                        className={cn(
                          "rounded-md border px-2.5 py-1.5 text-left transition-colors",
                          selectedId === q.id
                            ? "border-nymbl-navy bg-card shadow-sm"
                            : "border-transparent hover:border-[var(--color-border)] hover:bg-card",
                        )}
                      >
                        <div className="flex items-center gap-1.5">
                          {q.externalKey && (
                            <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                              {q.externalKey}
                            </span>
                          )}
                          <span
                            className={cn(
                              "inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                              PRIORITY_TINTS[q.priority],
                            )}
                          >
                            {q.priority}
                          </span>
                          <span
                            className={cn(
                              "inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-medium",
                              q.status === "answered"
                                ? "bg-success-bg text-success"
                                : "bg-muted text-foreground/70",
                            )}
                          >
                            {q.status === "answered" ? "Answered" : "Open"}
                          </span>
                        </div>
                        <div className="text-[11px] text-foreground/80 mt-1 max-w-[260px] truncate">
                          {q.text}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            <div className="flex-1 overflow-y-auto">
              <QuestionDetailContent
                questionId={selectedId}
                solutionId={solutionId}
                onDeleted={() => {
                  // Move to next remaining linked question, or close if none left.
                  const remaining = linked.filter((q) => q.id !== selectedId);
                  if (remaining.length === 0) {
                    onOpenChange(false);
                    setSelectedId(null);
                  } else {
                    setSelectedId(remaining[0]?.id ?? null);
                  }
                }}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
